import { FormattedMessage, useIntl } from "react-intl";

import ClientAvatar from "@components/ClientAvatar";
import ExpertAvatar from "@components/ExpertAvatar";
import InputSelect from "@components/fieldsNative/InputSelect";
import { InboxContext } from "@components/messaging/InboxProvider";
import InternalLink from "@components/typography/InternalLink";
import useContextSafely from "@hooks/useContextSafely";
import useMe from "@hooks/useMe";
import useMyClient from "@hooks/useMyClient";
import { CarePlanSectionEnum } from "@typing/Enums";
import { ChatFragment } from "@typing/Generated";
import { Colors } from "@utils/colorUtils";
import { expertRolesWithoutBackups } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  chat: ChatFragment;
  className?: string;
};

const AddRecipients = ({ chat, className }: Props) => {
  const intl = useIntl();
  const me = useMe();
  const myClient = useMyClient();
  const { journey, onChatClientsChange } = useContextSafely(InboxContext);

  const handleChatClientsChange = (values: string[]) => {
    onChatClientsChange(values.filter(v => journey?.clientRoles.some(cr => cr.client.id === v)));
  };

  const colorOrder: Colors[] = ["secondary-100", "secondary", "primary-300", "primary"];

  const expertOptions =
    expertRolesWithoutBackups(journey?.expertRoles)
      ?.filter(er => er.expert.id !== me?.id)
      .map(er => ({
        image: <ExpertAvatar expert={er.expert} size={30} />,
        isFixed: true,
        label: er.expert.firstName,
        value: er.expert.id
      })) ?? [];

  const clientOptions = journey?.clientRoles
    .filter(role => role.client.id !== myClient?.id)
    .map((cr, index) => ({
      image: (
        <ClientAvatar
          clientId={cr.client.id}
          color={colorOrder[index % colorOrder.length]}
          firstName={cr.client.firstName}
          journeyId={journey.id}
          lastName={cr.client.lastName}
        />
      ),
      isFixed: cr.client.id === journey.owner.id,
      label: cr.client.firstName,
      value: cr.client.id
    }));

  const newChatIds = Array.from(
    new Set([...expertOptions.map(option => option.value), ...chat.clients.map(client => client.id), journey?.owner.id])
  );

  return (
    <InputSelect
      className={className}
      noOptionsLabel={
        intl.formatMessage(
          { id: "pages.messages.noClients" },
          {
            careTeam: (
              <InternalLink
                linkEventCreateVariables={{ journeyId: journey?.id }}
                to={journeyRoute({
                  journeyId: journey?.id,
                  linkAfterJourney: `/care-plan?section=${CarePlanSectionEnum.CARE_TEAM}`,
                  myClient
                })}
              >
                <FormattedMessage id="pages.messages.careTeam" />
              </InternalLink>
            )
          }
        ) as string
      }
      onChange={handleChatClientsChange}
      options={[...expertOptions, ...clientOptions]}
      placeholder={intl.formatMessage({ id: "pages.messages.typeName" })}
      values={newChatIds}
    />
  );
};

export default AddRecipients;
